<template>
  <div class="asc-maintenance-grid">
    <template v-if="exportButtonVisible">
      <asc-grid-wrapper-header>
        <v-row no-gutters>
          <v-col v-if="maintenanceExportTableGridPermission">
            <asc-menu-button
              :disabled="agGridRowData && agGridRowData.length === 0"
              :items="exportMenuItems"
              :on-selection="handleSelection"
              :secondary="true"
            ></asc-menu-button>
          </v-col>
          <v-col
            v-if="
              hasResourcePermission({
                resource: resources.MANUAL_MAINTENANCE_REC,
                permissionLevel: permissionLevelConsts.EDIT,
              })
            "
          >
            <asc-accent-button
              class="pr-2"
              button-text="NEW PHM ISSUE"
              icon="mdi-plus"
              icon-position="left"
              :on-click="handleAddManualMxRec"
            ></asc-accent-button>
          </v-col>
          <v-col
            v-if="
              hasResourcePermission({
                resource: resources.MANUAL_REPEATER_MAINTENANCE_REC,
                permissionLevel: permissionLevelConsts.EDIT,
              }) && hasUserAirlineAgreement
            "
          >
            <asc-accent-button
              class="pr-2"
              button-text="NEW ISSUE"
              icon="mdi-plus"
              icon-position="left"
              :on-click="handleAddRepeaterIssue"
            ></asc-accent-button>
          </v-col>
        </v-row>
      </asc-grid-wrapper-header>
    </template>
    <asc-ag-grid-filter
      v-if="maintenanceGridPermission"
      :key="`asc-maintenance-grid_${gridId}`"
      :grid-options="agGridOptions"
      :column-defs="getGridColumnDefs"
      :row-data="agGridRowData"
    ></asc-ag-grid-filter>
  </div>
</template>

<script>
import {
  AscAgGridFilter,
  AscAgGridAutocompletePersistentFloatFilter,
  AscAgGridDatePersistentFloatFilter,
  AscDrillDownCellRenderer,
  AscDrillDownReadCellRenderer,
  AscTooltipCellRenderer,
} from '@/components/agGrid'
import AscGridWrapperHeader from './AscGridWrapperHeader'
import { AscGridPropsMixin, AscGridMethodsMixin } from '@/components/mixins'
import AscMenuButton from '@/components/atoms/AscMenuButton'
import AscAccentButton from '@/components/atoms/AscAccentButton'
import { getFormattedDateTime } from '@/utilities/dates'
import {
  MAINTENANCE_GRID_SORT_COLUMNS,
  PERMISSION_LEVEL_CONSTS,
  RESOURCES,
} from '@/utilities/appConsts'
import { getMaintenanceStatusValue } from '@/utilities/maintenance'
import {
  stringTextMatcher,
  arrayTextMatcher,
  dateStringMatcher,
} from '@/components/agGrid/AgGridHelperMethods'
import { getFilterOptions } from '@/components/agGrid/AgGridOptions'

export default {
  name: 'AscMaintenanceGrid',

  components: {
    AscAgGridFilter,
    AscGridWrapperHeader,
    AscMenuButton,
    AscAccentButton,
    AscDrillDownCellRenderer,
    AscDrillDownReadCellRenderer,
    AscAgGridAutocompletePersistentFloatFilter,
    AscAgGridDatePersistentFloatFilter,
    AscTooltipCellRenderer,
  },

  mixins: [AscGridPropsMixin, AscGridMethodsMixin],

  props: {
    onMxRecAlertsExport: {
      type: Function,
      default: () => {},
    },
    exportMenuItems: {
      type: Array,
      default: () => [],
    },
    onAddManualMxRec: {
      type: Function,
      default: () => {},
    },
    maintenanceExportTableGridPermission: {
      type: Boolean,
      default: false,
    },
    maintenanceGridPermission: {
      type: Boolean,
      default: false,
    },
    gridHeight: {
      type: String,
      default: 'lg',
    },
    persistentGridId: {
      type: String,
      default: '',
    },
    hasUserAirlineAgreement: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      localStorageName: 'maintenance_grid_read_ids',
      updateDateFieldName: 'lastAlertDate',
      agGridOptions: getFilterOptions(this, {
        defaultColDef: {
          tooltipComponent: 'AscTooltipCellRenderer',
        },
        getContextMenuItems: this.getContextMenuItems,
        onGridReady: this.handleGridReady,
        rowClass: 'highlightHoveredRows',
        onFirstDataRendered: this.handleFirstDataRendered,
        onGridSizeChanged: this.handleGridSizeChanged,
        onCellClicked: this.handleCellClicked,
        onSortChanged: this.handleGridSortChange, // This is required id for persistent filters to update on change
        onFilterChanged: this.handleGridFilterChange, // This is required id for persistent filters to update on change
        onRowClicked: this.handleRowClicked,
      }),
      statusColumnSortOrder: MAINTENANCE_GRID_SORT_COLUMNS,
      permissionLevelConsts: PERMISSION_LEVEL_CONSTS,
      resources: RESOURCES,
    }
  },

  computed: {
    mappedValues() {
      return this.values.map(item => {
        let displayName =
          item.analyticModule !== null && item.analyticModule !== 'N/A'
            ? item.analyticModule
            : null

        if (displayName === null) {
          displayName = item.customAlertName ? item.customAlertName : null
        }

        return {
          ...item,
          analyticDisplayName: displayName === null ? '-' : displayName,
        }
      })
    },
    agGridRowData() {
      this.resetLocalStorage(this.localStorageName)

      return this.mappedValues
    },
    getGridColumnDefs() {
      return [
        {
          headerName: this.getColumnTitle(this.gridColumns, 'issueId', 'ID'),
          field: 'issueId',
          cellClass: this.isColumnDrillSupported(this.gridColumns, 'issueId')
            ? ['ag-cell--drill', 'ag-cell--drill-ripple']
            : null,
          cellRenderer: this.isColumnDrillSupported(this.gridColumns, 'issueId')
            ? 'AscDrillDownCellRenderer'
            : null,
          cellRendererParams: {
            localStorageName: this.localStorageName,
            updateDateFieldName: this.updateDateFieldName,
          },
          filter: 'agNumberColumnFilter',
          hide: !this.isColumnVisible(this.gridColumns, 'issueId'),
          initialWidth: 50,
          minWidth: 50,
          maxWidth: 75,
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'statusName',
            'Status',
          ),
          field: 'statusName',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompletePersistentFloatFilter',
          floatingFilterComponentParams: {
            persistentGridId: this.persistentGridId,
            ids: this.getFloatFilterStatus(),
            selectedFilterValue: this.$route.query.status,
            items: this.getUniqueCustomSortItems(
              'statusName',
              this.statusColumnSortOrder,
            ),
            suppressFilterButton: true,
            suppressDefaultSort: true,
          },
          comparator: function (status1, status2) {
            const status1Value = getMaintenanceStatusValue(status1)
            const status2Value = getMaintenanceStatusValue(status2)
            return status2Value - status1Value
          },
          hide: !this.isColumnVisible(this.gridColumns, 'statusName'),
          wrapText: true,
          cellStyle: { 'word-break': 'normal' },
          autoHeight: true,
          initialWidth: 140,
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'airlineName',
            'Airline',
          ),
          field: 'airlineName',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompletePersistentFloatFilter',
          floatingFilterComponentParams: {
            persistentGridId: this.persistentGridId,
            ids: [],
            items: this.getUniqueItems('airlineName'),
            suppressFilterButton: true,
          },
          hide: !this.isColumnVisible(this.gridColumns, 'airlineName'),
          initialWidth: 50,
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'tailNumber',
            'Tail',
          ),
          field: 'tailNumber',
          cellClass: this.isColumnDrillSupported(this.gridColumns, 'tailNumber')
            ? ['ag-cell--drill', 'ag-cell--drill-ripple']
            : null,
          cellRenderer: this.isColumnDrillSupported(
            this.gridColumns,
            'tailNumber',
          )
            ? 'AscDrillDownCellRenderer'
            : null,
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompletePersistentFloatFilter',
          floatingFilterComponentParams: {
            persistentGridId: this.persistentGridId,
            ids: [],
            items: this.getUniqueItems('tailNumber'),
            suppressFilterButton: true,
          },
          hide: !this.isColumnVisible(this.gridColumns, 'tailNumber'),
          initialWidth: 80,
          minWidth: 60,
          maxWidth: 120,
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'analyticDisplayName',
            'Analytic',
          ),
          field: 'analyticDisplayName',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompletePersistentFloatFilter',
          floatingFilterComponentParams: {
            persistentGridId: this.persistentGridId,
            ids: [],
            items: this.getUniqueItems('analyticDisplayName'),
            suppressFilterButton: true,
          },
          hide: !this.isColumnVisible(this.gridColumns, 'analyticDisplayName'),
          wrapText: true,
          cellStyle: { 'word-break': 'normal' },
          autoHeight: true,
          initialWidth: 250,
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'masterModelName',
            'Master Model',
          ),
          field: 'masterModelName',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompletePersistentFloatFilter',
          floatingFilterComponentParams: {
            persistentGridId: this.persistentGridId,
            ids: [],
            items: this.getUniqueItems('masterModelName'),
            suppressFilterButton: true,
          },
          hide: !this.isColumnVisible(this.gridColumns, 'masterModelName'),
          initialWidth: 75,
          minWidth: 60,
          maxWidth: 120,
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'ataChapter',
            'ATA CHAPTER',
          ),
          field: 'ataChapter',
          filter: 'agTextColumnFilter',
          hide: !this.isColumnVisible(this.gridColumns, 'ataChapter'),
          initialWidth: 120,
          minWidth: 40,
          maxWidth: 120,
          wrapHeaderText: true,
          autoHeaderHeight: true,
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'fmtOwnerName',
            'AlertOwner',
          ),
          field: 'fmtOwnerName',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompletePersistentFloatFilter',
          floatingFilterComponentParams: {
            persistentGridId: this.persistentGridId,
            ids: [],
            items: this.getUniqueItems('fmtOwnerName'),
            suppressFilterButton: true,
          },
          hide: !this.isColumnVisible(this.gridColumns, 'fmtOwnerName'),
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'alertType',
            'Issue Source',
          ),
          field: 'alertType',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: arrayTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompletePersistentFloatFilter',
          floatingFilterComponentParams: {
            persistentGridId: this.persistentGridId,
            ids: [],
            items: this.getUniqueItems('alertType'),
            suppressFilterButton: true,
          },
          hide: !this.isColumnVisible(this.gridColumns, 'alertType'),
          initialWidth: 50,
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'alertCount',
            'Alert Count',
          ),
          field: 'alertCount',
          filter: 'agNumberColumnFilter',
          hide: !this.isColumnVisible(this.gridColumns, 'alertCount'),
          initialWidth: 120,
          minWidth: 40,
          maxWidth: 120,
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'lastAlertDate',
            'Date Last Alert',
          ),
          field: 'lastAlertDate',
          width: 155,
          minWidth: 155,
          sort: 'desc',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: dateStringMatcher,
          },
          floatingFilterComponent: 'AscAgGridDatePersistentFloatFilter',
          floatingFilterComponentParams: {
            persistentGridId: this.persistentGridId,
            suppressFilterButton: true,
            items: this.values,
            dateFloatFilterConfig: {
              left: true,
              transitionOrigin: 'top right',
            },
          },
          valueFormatter: data => getFormattedDateTime(data.value),
          comparator: this.sortDate,
          hide: !this.isColumnVisible(this.gridColumns, 'lastAlertDate'),
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'analyticAlert',
            'Analytic Alert',
          ),
          field: 'analyticAlert',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompletePersistentFloatFilter',
          floatingFilterComponentParams: {
            persistentGridId: this.persistentGridId,
            ids: [],
            items: this.getUniqueItems('analyticAlert'),
            suppressFilterButton: true,
          },
          hide: !this.isColumnVisible(this.gridColumns, 'analyticAlert'),
        },
      ]
    },
  },

  watch: {
    values() {
      this.handleRefreshColWidths()
    },
  },

  methods: {
    getFloatFilterStatus() {
      const array = []
      const status = this.$route.query.status
      if (status && status.length > 0) {
        array.push(status)
      }
      return array.length > 0
        ? array
        : this.getUniqueItems('statusName').map(x => x.text)
    },
    handleRowClicked(event) {
      this.$router.push({
        name: 'Mx Recs',
        params: {
          issueId: event.data.id,
          repeaterId: event.data?.uniqueChronicId,
        },
        query: event.data?.uniqueChronicId
          ? { repeaterId: event.data?.uniqueChronicId }
          : undefined,
      })
      this.updateLocalStorage(
        this.localStorageName,
        this.updateDateFieldName,
        event,
      )
    },
    handleCellClicked(event) {
      if (event) {
        const field = event.colDef.field
        switch (field) {
          case 'tailNumber':
            if (this.isColumnDrillSupported(this.gridColumns, 'tailNumber')) {
              this.$router.push({
                name: 'Fleet',
                params: {
                  tailNumber: event.value,
                },
              })
            }
            break
          case 'componentName':
            if (
              this.isColumnDrillSupported(this.gridColumns, 'componentName')
            ) {
              this.$router.push({
                name: 'Component',
                params: {
                  componentId: event.data.componentId,
                },
              })
            }
            break
          default:
            break
        }
      }
    },

    getContextMenuItems(params) {
      const _that = this
      const issueId = params.node.data.issueId
      const lastAlertDate = params.node.data.lastAlertDate
      const event = {
        value: issueId,
        data: {
          lastAlertDate,
        },
      }
      const url = `maintenance/issueid/${issueId}`
      const result = [
        {
          name: 'Open Link in New Tab',
          action: function () {
            _that.updateLocalStorage(
              _that.localStorageName,
              _that.updateDateFieldName,
              event,
            )
            window.open(url, '_blank', 'noopener', 'noreferrer')
          },
        },
        'separator',
        'copy',
        'copyWithHeaders',
        'export',
      ]

      return result
    },

    handleSelection(selection) {
      if (selection && selection.value === 1) {
        if (this.onMxRecAlertsExport) {
          this.onMxRecAlertsExport()
        }
      } else {
        this.agGridOptions.api.exportDataAsCsv()
      }
    },

    handleAddManualMxRec() {
      if (this.onAddManualMxRec) {
        this.onAddManualMxRec()
      }
    },

    handleAddRepeaterIssue() {
      this.$emit('onAddRepeaterIssue')
    },
  },
}
</script>

<style lang="scss">
.asc-maintenance-grid {
  .asc-sort-icon {
    color: $primary_black;
    position: absolute;
    top: 14px;
    right: 16px;
  }

  .asc-filter-icon {
    color: $primary_black;
  }

  .asc-grid-wrapper-header--container {
    .asc-grid-wrapper-header-buttons--container {
      .row {
        .col:first-child {
          margin-right: 10px;
        }
      }
    }
  }
}
</style>
